import { siteConfig } from '@/config/site'
import { camelizeKeys, snakelizeKeys } from '@/lib/utils'
import {
  BaseUrlRequest,
  BaseUrlResponse,
  Channel,
  PurchasePremiumResponse,
} from '@/types/channel'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import Cookies from 'js-cookie'
import { omit, pathOr } from 'ramda'

import { axiosInstance } from '../axios'

export const fetchChannels = async ({
  params,
}: {
  params: Record<string, string | boolean | number | Array<number>>
}): Promise<{ list: Channel[]; pagination: any } | undefined> => {
  try {
    const config = {
      params: omit(['categoryIds', 'showSexualTagsOnly', 'userId'], params),
    }

    let categoryIdsString = ''

    const categoryIds = pathOr([], ['categoryIds'], params)
    const hasCategories = categoryIds.length > 0

    const selectedIds =
      pathOr(false, ['showSexualTagsOnly'], params) && hasCategories
        ? categoryIds?.filter((id: number) => {
            return siteConfig.sexualPreferenceCategoryIds.includes(id)
          })
        : params.categoryIds

    if (hasCategories) {
      categoryIdsString = selectedIds
        // @ts-ignore
        .map((id: number) => `category_id=${id}`)
        .join('&')
    }

    const url = hasCategories ? `/channels?${categoryIdsString}` : '/channels'

    const response = await axiosInstance.get<
      any,
      { list: Channel[]; pagination: any }
    >(url, config)
    return response
  } catch (error) {
    console.error('🚀 ~ error:', error)
  }
}

const fetchChannel = async ({
  channelId,
}: {
  channelId: number | null
}): Promise<Channel | undefined> => {
  try {
    const resData = await axiosInstance.get<any, Channel>(
      `/channels/${channelId}`,
    )
    return camelizeKeys(resData)
  } catch (error) {
    console.error('🚀 ~ error:', error)
  }
}

type ChannelRelation = {
  name: string
  relateId: number
  relateType: string
}

const fetchChannelIdsByCodes = async ({
  channelCodes,
}: {
  channelCodes: string[]
}): Promise<Channel[] | undefined> => {
  try {
    const resData = await axiosInstance.post<any, Channel[]>(`/codes`, {
      codes: channelCodes,
    })
    const result = camelizeKeys(resData)
    const channelIds = result?.list?.map(
      (item: ChannelRelation) => item.relateId,
    )

    console.log('🚀 ~ fetchChannelIdsByCodes ~ result:', channelIds)
    return channelIds
  } catch (error) {
    console.error('🚀 ~ error:', error)
  }
}
export function useChannelIds({ channelCodes }: { channelCodes: string[] }) {
  return useQuery({
    queryKey: ['channel', channelCodes, 'codes'],
    queryFn: () =>
      fetchChannelIdsByCodes({
        channelCodes,
      }),
    retry: false,
  })
}

export function useChannels({
  params = {},
}: {
  params?: Record<string, string | number | number[] | boolean>
}) {
  return useInfiniteQuery({
    queryKey: ['channels', params, params?.userId],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchChannels({
        params: {
          ...params,
          page: pageParam,
        },
      }),
    retry: false,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined
      // 如果當前頁是最後一頁，則返回 undefined
      // @ts-ignore
      return lastPage.pagination.currentPage < lastPage?.pagination?.totalPages
        ? // @ts-ignore
          lastPage.pagination.nextPage
        : undefined
    },
  })
}

export function useMyChannels() {
  return useQuery({
    queryKey: ['channels', 'me'],
    queryFn: () => {
      const token = Cookies.get(siteConfig.tokenKey || '')
      if (!token) {
        return { list: [], pagination: {} }
      }
      return fetchChannels({ params: { owned: true } })
    },
    enabled: !!Cookies.get(siteConfig.tokenKey || ''),
  })
}

export function useChannel({ channelId }: { channelId: number | null }) {
  return useQuery({
    queryKey: ['channel', channelId],
    queryFn: () => fetchChannel({ channelId }),
    enabled: !!channelId,
  })
}

export function CreateChannel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (formData): Promise<any> => {
      const res = await axiosInstance.post(`/channels`, formData)
      // @ts-ignore
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'me'] })
    },
  })
}

export function useUpdateChannel({ channelId }: { channelId: number }) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (formData: Channel): Promise<any> => {
      const payload = {
        name: formData?.name,
        description: formData?.description,
        roles: formData?.roles,
      }
      const body = snakelizeKeys(payload)

      try {
        const res = await axiosInstance.put(`/channels/${channelId}`, body)
        return res
      } catch (error) {
        console.log('🚀 ~ mutationFn: ~ error:', error)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'me'] })
    },
  })
}

export function GetChannelsRole() {
  return useQuery({
    queryKey: ['channels', 'role'],
    queryFn: () => axiosInstance.get('/channels/role'),
  })
}

export function DeleteChannel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: id => axiosInstance.delete(`/channels/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'me'] })
    },
  })
}

// 取得上傳頭像的 url
export function useAvatarUploadUrl({ channelId }: { channelId: number }) {
  return useMutation({
    mutationFn: async ({ name }: BaseUrlRequest): Promise<BaseUrlResponse> => {
      const resData = await axiosInstance.post<any, BaseUrlResponse>(
        `/channels/${channelId}/avatar/upload-url`,
        { name: name.toLowerCase() },
      )
      // @ts-ignore
      return resData
    },
  })
}

// 取得上傳封面的 url
export function useCoverUploadUrl({ channelId }: { channelId: number }) {
  return useMutation({
    mutationFn: async ({ name }: BaseUrlRequest): Promise<BaseUrlResponse> => {
      const resData = await axiosInstance.post<any, BaseUrlResponse>(
        `/channels/${channelId}/cover/upload-url`,
        { name: name.toLowerCase() },
      )
      // @ts-ignore
      return resData
    },
  })
}

export function usePurchasePremium() {
  return useMutation({
    mutationFn: ({
      channelId,
      premiumId,
      returnUrl,
      payType,
      promoCode,
    }: {
      channelId: number
      premiumId: number
      returnUrl: string
      payType: string
      promoCode?: string
    }) => {
      const body = snakelizeKeys({
        channelId,
        premiumId,
        payType,
        source: 'premium',
        returnUrl,
        promoCode,
      })

      return axiosInstance.post<any, PurchasePremiumResponse>(
        `/purchase/premium`,
        body,
      )
    },
  })
}

interface ChannelPaymentsPayload {
  channelId: number
  premiumId: number
  promoCode?: string
}
export function useChannelPayments(payload: ChannelPaymentsPayload) {
  const { channelId, premiumId, promoCode } = payload
  return useQuery({
    queryKey: ['channel-payments', channelId, premiumId],
    queryFn: () => {
      return axiosInstance.get(`/purchase/payChannel/premium`, {
        params: {
          ...(promoCode && { promo_code: promoCode }),
          channel_id: channelId,
          premium_id: premiumId,
        },
      })
    },
    enabled: !!channelId || !!premiumId,
  })
}

export function useChannelFilesCheck(channelId: number) {
  return useMutation({
    mutationFn: ({ file_ids }: { file_ids: number[] | string[] }) =>
      axiosInstance.post(`channels/${channelId}/files/check-used`, {
        file_ids,
      }),
  })
}
